/* app.jsx — application shell wired to the live backend (window.API). */
const { useState, useMemo, useEffect, useCallback, useRef } = React;

const NAV = [
  { group: "Command Center", items: [
    { id: "actionCenter", label: "Action Center", icon: "handover" },
    { id: "appligatorFactory", label: "Appligator Factory", icon: "tool" },
    { id: "appligatorOffice", label: "Appligator Office", icon: "server" },
    { id: "appligatorFactoryGraphics", label: "Appligator Factory Graphics", icon: "palette" },
    { id: "dashboard", label: "Business Overview", icon: "dashboard" },
  ] },
  { group: "Delivery", items: [
    { id: "projects", label: "Projects", icon: "projects" },
    { id: "documents", label: "Documents", icon: "doc" },
    { id: "implementationGuidelines", label: "Guidelines", icon: "doc" },
  ] },
  { group: "Communication", items: [
    { id: "calendar", label: "Calendar", icon: "calendar" },
    { id: "email", label: "Email", icon: "mail" },
    { id: "customers", label: "Customers", icon: "customers" },
  ] },
  { group: "Finance & People", items: [
    { id: "invoices", label: "Invoices", icon: "invoices" },
    { id: "payments", label: "Payments", icon: "wallet" },
    { id: "expenses", label: "Expenses", icon: "expenses" },
    { id: "employees", label: "Employees", icon: "employees" },
  ] },
];

const VIEW_META = {
  dashboard: ["Overview", "Cashflow, receivables & operating movement"],
  actionCenter: ["Action Center", "Dispatch work to AI employees and monitor live task runs"],
  appligatorFactory: ["Appligator Factory", "Live factory floor from the Action Center push feed"],
  appligatorFactoryGraphics: ["Appligator Factory Graphics", "Graphics runtime view for the factory floor"],
  appligatorOffice: ["Appligator Office", "Read-only global Action Center debugger"],
  implementationGuidelines: ["Guideline Templates", "Project planning and implementation agent context"],
  documents: ["Documents", "All project documents and external sources"],
  projects: ["Projects", "Project records, deliverables & linked customers"],
  customers: ["Customers", "Billing details & notes"],
  calendar: ["Calendar", "Meetings, project checkpoints & internal calls"],
  email: ["Email", "Inbox, message details & replies"],
  invoices: ["Invoices", "Drafts, finalization & payments"],
  expenses: ["Expenses", "Operating spend, wages & recurring costs"],
  payments: ["Payments", "Outgoing expense payment queue"],
  employees: ["Employees", "Staff records & payroll"],
  settings: ["Settings", "Company profile & billing defaults"],
};

function milestoneInvoiceAmount(milestone) {
  return (Number(milestone.actualCost) || 0)
    || ((Number(milestone.actualHours) || 0) * (Number(milestone.hourlyRate) || 0))
    || ((Number(milestone.estimatedHours) || 0) * (Number(milestone.hourlyRate) || 0));
}

const TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "sidebar": "dark",
  "accent": "#2856c4",
  "density": "regular"
}/*EDITMODE-END*/;

function viewFromHash() {
  const view = window.location.hash.replace(/^#\/?/, "").split("/")[0];
  return VIEW_META[view] ? view : "dashboard";
}

function AppligatorFactoryGraphicsUnavailable() {
  return (
    <section className="factory-package-page">
      <div className="factory-empty">
        <strong>Factory graphics unavailable</strong>
        <span>The graphics runtime has not been loaded.</span>
      </div>
    </section>
  );
}

function projectIdFromHashForShell(hash) {
  const match = String(hash || "").match(/^#\/?projects\/([^/]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function activeActionCenterRun(run) {
  const status = String(run && run.status || "").trim().toLowerCase();
  return !!run && !["done", "error"].includes(status);
}

function readyForReviewThreadCount(db) {
  const projects = db && Array.isArray(db.projects) ? db.projects : [];
  if (!projects.length || !window.MODEL || !window.MODEL.projectTickets) return 0;
  return projects.reduce((count, project) => {
    const tickets = window.MODEL.projectTickets(project) || [];
    return count + tickets.filter((ticket) => String(ticket && ticket.status || "").trim().toLowerCase() === "ready for review").length;
  }, 0);
}

function isActionCenterOverviewChange(change) {
  const toolName = String(change && change.toolName || "").trim().toLowerCase();
  return toolName.startsWith("action-center/");
}

function isActionCenterTransientActivity(change) {
  const toolName = String(change && change.toolName || "").trim().toLowerCase();
  return change && change.kind === "employee-activity" && change.jobId && (toolName === "transient-activity" || toolName === "codex-progress");
}

function isProjectTicketChange(change) {
  const toolName = String(change && change.toolName || "").trim().toLowerCase();
  return toolName.startsWith("project_ticket_") || toolName.startsWith("project_tickets_");
}

function isProjectSketchChange(change) {
  const toolName = String(change && change.toolName || "").trim().toLowerCase();
  return toolName.startsWith("project_sketch_") || toolName.startsWith("project_sketches_");
}

function isProjectContextFileChange(change) {
  const toolName = String(change && change.toolName || "").trim().toLowerCase();
  return toolName === "document_upload"
    || toolName === "document_update"
    || toolName === "document_delete"
    || toolName.startsWith("document_project_");
}

function changeDetails(change) {
  return (change && (change.details || change.Details)) || {};
}

function changeDetailValue(change, key) {
  const details = changeDetails(change);
  const pascal = key.charAt(0).toUpperCase() + key.slice(1);
  return details ? details[key] ?? details[pascal] : undefined;
}

function progressEventText(event) {
  return String((event && (event.speechText ?? event.delta ?? event.text)) || "").trim();
}

function isLiveSpeechProgressEvent(event) {
  if (!event) return false;
  const text = progressEventText(event);
  if (!text) return false;
  const method = String(event.method || "").trim().toLowerCase();
  const messageType = String(event.messageType || "").trim().toLowerCase();
  const toolName = String(event.toolName || "").trim().toLowerCase();
  return messageType === "assistant_response"
    || messageType === "assistant_thinking"
    || method === "item/agentmessage/delta"
    || method === "item/agent_message/delta"
    || method === "item/reasoning/summarypartadded"
    || method === "item/reasoning/summarytextdelta"
    || toolName === "assistant_response"
    || toolName === "assistant_thinking";
}

function liveSpeechProgressText(event) {
  return isLiveSpeechProgressEvent(event) ? progressEventText(event) : "";
}

function latestStreamTextFromLiveActivity(activity) {
  const events = activity && Array.isArray(activity.events) ? activity.events : [];
  const responseEvent = events.find(isLiveSpeechProgressEvent);
  return liveSpeechProgressText(responseEvent);
}

function tailText(value, maxLength) {
  const text = String(value || "").trim();
  if (text.length <= maxLength) return text;
  return `...${text.slice(-maxLength).trimStart()}`;
}

function toolNameFromProgressMessage(message) {
  const text = String(message || "").trim();
  const calling = text.match(/^Calling\s+(.+?)(?:\.\.\.)?$/i);
  if (calling && calling[1]) return calling[1].trim();
  if (/^Preparing (a )?command/i.test(text) || /^Running command:/i.test(text)) return "exec_command";
  if (/^(Editing files|File change output|Patch updated)/i.test(text)) return "file_change";
  if (/^(Thinking|Reasoning)/i.test(text)) return "assistant_thinking";
  if (/^Searching tools/i.test(text)) return "tool_search";
  if (/^Searching the web/i.test(text)) return "web_search";
  return "";
}

function toolNameFromCodexProgress(change, eventKind, detailsMessageType, detailsToolName, command, eventMethod) {
  if (detailsToolName) return detailsToolName;
  if (detailsMessageType) return detailsMessageType;
  const itemType = String(changeDetailValue(change, "itemType") || "").trim();
  const cleanItemType = itemType.toLowerCase();
  const cleanMethod = String(eventMethod || "").toLowerCase();
  if (command) return "exec_command";
  if (cleanItemType === "commandexecution" || cleanItemType === "local_shell_call") return "exec_command";
  if (cleanItemType === "filechange" || cleanMethod.includes("filechange")) return "file_change";
  if (cleanItemType === "websearch" || cleanItemType === "web_search_call") return "web_search";
  if (cleanItemType === "tool_search_call") return "tool_search";
  if (cleanItemType === "mcptoolcall" || cleanItemType === "dynamictoolcall" || cleanItemType === "collabtoolcall") return "tool_call";
  if (cleanMethod.includes("reasoning/")) return "assistant_thinking";
  if (cleanMethod.includes("agentmessage/delta") || cleanMethod.includes("agent_message/delta")) return "assistant_response";
  if (String(eventKind || "").toLowerCase() === "assistant-delta") return "assistant_response";
  return toolNameFromProgressMessage(change && change.message);
}

function actionCenterActivityFromChange(change, liveActivity) {
  if (!change) return null;
  const kind = String(change.kind || "").trim().toLowerCase();
  const eventKind = String(change.toolName || "").trim();
  const detailsMessageType = String(changeDetailValue(change, "messageType") || "").trim();
  const detailsToolName = String(changeDetailValue(change, "toolName") || "").trim();
  const toolArgumentsJson = String(changeDetailValue(change, "toolArgumentsJson") || "").trim();
  const command = String(changeDetailValue(change, "command") || "").trim();
  const eventMethod = String(changeDetailValue(change, "eventMethod") || "").trim().toLowerCase();
  const inferredToolName = toolNameFromCodexProgress(change, eventKind, detailsMessageType, detailsToolName, command, eventMethod);
  const responseStreamText = latestStreamTextFromLiveActivity(liveActivity);
  const plan = liveActivity && Array.isArray(liveActivity.plan) ? liveActivity.plan : [];
  let toolName = inferredToolName;
  if (!toolName && kind === "tool-call") toolName = eventKind;
  if (!toolName && kind === "employee-activity" && eventKind && !["transient-activity", "codex-progress", "assistant-delta"].includes(eventKind.toLowerCase())) toolName = eventKind;
  if (!toolName && plan.length) toolName = "codex-progress";
  if (!toolName) return null;
  const streamText = String(toolName).toLowerCase() === "assistant_response" ? responseStreamText : "";
  const toolText = String(change.message || "").trim();
  return {
    messageType: detailsMessageType,
    toolName,
    message: streamText || toolText,
    streamText,
    streamPreviewText: tailText(streamText, 120),
    streamExpandedText: tailText(streamText, 520),
    toolText,
    plan,
    toolArgumentsJson,
    command,
    runId: change.jobId || "",
    changedAt: change.changedAt || new Date().toISOString(),
  };
}

function actionCenterRouteFromHash(hash) {
  const parts = String(hash || "").replace(/^#\/?/, "").split("/");
  if (parts[0] !== "actionCenter") return { employeeId: "", runId: "" };
  return {
    employeeId: parts[1] ? decodeURIComponent(parts[1]) : "",
    runId: parts[2] ? decodeURIComponent(parts[2]) : "",
  };
}

function actionCenterThreadHash(employeeId, runId) {
  return `#actionCenter/${encodeURIComponent(employeeId || "")}/${encodeURIComponent(runId || "")}`;
}

const BRIDGE_ACTIVITY_PULSE_MS = 12000;
const ACTION_CENTER_BRIDGE_ACTIVITY_EVENT = "action-center-bridge-activity";

function TopbarAiEmployeeActivity({ overview, bridgeActiveEmployeeIds, bridgeActivityByEmployeeId, onOpenRun }) {
  const [openAgentId, setOpenAgentId] = useState("");
  const rootRef = useRef(null);
  const agents = overview && Array.isArray(overview.agents) ? overview.agents : [];
  const activeRuns = useMemo(() => (overview && Array.isArray(overview.runs) ? overview.runs : []).filter(activeActionCenterRun), [overview]);
  const runsByAgent = useMemo(() => {
    const grouped = new Map();
    activeRuns.forEach((run) => {
      const employeeId = run.employeeId || run.agentId;
      if (!employeeId) return;
      grouped.set(employeeId, [...(grouped.get(employeeId) || []), run]);
    });
    grouped.forEach((runs) => runs.sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0)));
    return grouped;
  }, [activeRuns]);
  const activeAgents = useMemo(() => agents
    .filter((agent) => (runsByAgent.get(agent.id) || []).length > 0)
    .sort((a, b) => {
      const ar = runsByAgent.get(a.id) || [];
      const br = runsByAgent.get(b.id) || [];
      return new Date((br[0] && (br[0].updatedAt || br[0].createdAt)) || 0) - new Date((ar[0] && (ar[0].updatedAt || ar[0].createdAt)) || 0);
    }), [agents, runsByAgent]);
  const openAgent = activeAgents.find((agent) => agent.id === openAgentId) || null;
  const openRuns = openAgent ? (runsByAgent.get(openAgent.id) || []) : [];

  useEffect(() => {
    if (!openAgentId) return undefined;
    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpenAgentId("");
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpenAgentId("");
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [openAgentId]);

  if (!activeAgents.length) return null;

  function avatarFor(agent) {
    const communicating = !!(bridgeActiveEmployeeIds && bridgeActiveEmployeeIds[String(agent && agent.id)]);
    const activity = bridgeActivityByEmployeeId && bridgeActivityByEmployeeId[String(agent && agent.id)];
    return <AiEmployeeActivityAvatar employee={agent} communicating={communicating} activity={activity} />;
  }

  return (
    <div className="topbar-ai-work" ref={rootRef}>
      {activeAgents.map((agent) => {
        const runs = runsByAgent.get(agent.id) || [];
        return (
          <button
            key={agent.id}
            type="button"
            className={`topbar-ai-button ${openAgentId === agent.id ? "open" : ""}`}
            aria-label={`${agent.name}: ${runs.length} active Action Center thread${runs.length === 1 ? "" : "s"}`}
            aria-expanded={openAgentId === agent.id}
            onClick={() => setOpenAgentId((current) => current === agent.id ? "" : agent.id)}
          >
            {avatarFor(agent)}
            {runs.length > 1 && <span className="topbar-ai-count">{runs.length}</span>}
          </button>
        );
      })}
      {openAgent && (
        <div className="topbar-ai-menu" role="menu">
          <div className="topbar-ai-menu-head">
            {avatarFor(openAgent)}
            <div>
              <strong>{openAgent.name}</strong>
              <span>{openRuns.length} active thread{openRuns.length === 1 ? "" : "s"}</span>
            </div>
          </div>
          <div className="topbar-ai-run-list">
            {openRuns.map((run) => (
              <button
                key={run.id}
                type="button"
                className="topbar-ai-run"
                role="menuitem"
                onClick={() => {
                  setOpenAgentId("");
                  onOpenRun(openAgent.id, run.id);
                }}
              >
                <span className={`topbar-ai-status ${String(run.status || "queued").toLowerCase()}`} />
                <span>
                  <strong>{run.title || "Untitled task"}</strong>
                  <em>{run.status || "Queued"}{run.threadId ? ` - ${run.threadId}` : ""}</em>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function App() {
  const [t, setTweak] = useTweaks(TWEAK_DEFAULTS);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [db, setDb] = useState(null);
  const [session, setSession] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [view, setViewState] = useState(viewFromHash);
  const viewRef = useRef(view);
  const [routeHash, setRouteHash] = useState(() => window.location.hash);
  const actionCenterRoute = useMemo(() => actionCenterRouteFromHash(routeHash), [routeHash]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [query, setQuery] = useState("");
  const [drawer, setDrawer] = useState(null);
  const [toast, setToast] = useState(null);
  const [chatWindow, setChatWindow] = useState(null);
  const [voiceAssistantOpen, setVoiceAssistantOpen] = useState(false);
  const [voicePageContext, setVoicePageContext] = useState(null);
  const [unreadInboxCount, setUnreadInboxCount] = useState(0);
  const [actionCenterNeedsCount, setActionCenterNeedsCount] = useState(0);
  const [actionCenterOverview, setActionCenterOverview] = useState({ agents: [], runs: [], taggedTickets: [], needsInputCount: 0 });
  const [bridgeActiveEmployeeIds, setBridgeActiveEmployeeIds] = useState({});
  const [bridgeActivityByEmployeeId, setBridgeActivityByEmployeeId] = useState({});
  const [bridgeActivityByRunId, setBridgeActivityByRunId] = useState({});
  const [bridgeSpeechByRunId, setBridgeSpeechByRunId] = useState({});
  const actionCenterTransientActivitiesRef = useRef({});
  const bridgeActiveEmployeeIdsRef = useRef({});
  const bridgeActivityByEmployeeIdRef = useRef({});
  const bridgeActivityByRunIdRef = useRef({});
  const bridgeSpeechByRunIdRef = useRef({});
  const bridgeActivityTimersRef = useRef({});
  const bridgeActivityRunTimersRef = useRef({});

  useEffect(() => { viewRef.current = view; }, [view]);

  const reload = useCallback(async () => {
    try { const next = await window.API.loadDb(); setDb(next); setError(null); }
    catch (e) {
      if (e.status === 401) {
        setSession({ authenticated: false, user: null });
        setDb(null);
        setError(null);
      } else {
        setError(e.message || "Failed to load data.");
      }
    }
    finally { setLoading(false); }
  }, []);

  const refreshSession = useCallback(async () => {
    try { setSession(await window.API.session()); setError(null); }
    catch (e) { setError(e.message || "Failed to load session."); }
    finally { setAuthLoading(false); }
  }, []);

  const refreshActionCenterOverview = useCallback(async () => {
    if (!window.API.getActionCenter) return;
    try {
      const next = await window.API.getActionCenter();
      setActionCenterOverview(next || { agents: [], runs: [], taggedTickets: [], needsInputCount: 0 });
      setActionCenterNeedsCount(Number(next && next.needsInputCount) || 0);
    } catch (_) {
      setActionCenterOverview({ agents: [], runs: [], taggedTickets: [], needsInputCount: 0 });
      setActionCenterNeedsCount(0);
    }
  }, []);

  const markBridgeEmployeeActive = useCallback((employeeId, change, liveActivity) => {
    const key = String(employeeId || "").trim();
    if (!key) return;
    let activity = actionCenterActivityFromChange(change, liveActivity);
    const runKey = activity && activity.runId ? String(activity.runId).toLowerCase() : "";
    if (activity && runKey) {
      const previousRunActivity = bridgeActivityByRunIdRef.current[runKey];
      const speechText = String(activity.streamText || "").trim();
      if (speechText) {
        const nextSpeechActivity = {
          ...activity,
          streamText: speechText,
          toolName: activity.messageType || activity.toolName || "assistant_response",
          messageType: activity.messageType || activity.toolName || "assistant_response",
        };
        const nextSpeechByRunId = { ...bridgeSpeechByRunIdRef.current, [runKey]: nextSpeechActivity };
        bridgeSpeechByRunIdRef.current = nextSpeechByRunId;
        setBridgeSpeechByRunId(nextSpeechByRunId);
      }
      if ((!activity.plan || !activity.plan.length) && previousRunActivity && Array.isArray(previousRunActivity.plan) && previousRunActivity.plan.length) {
        activity = { ...activity, plan: previousRunActivity.plan };
      }
    }
    if (activity) {
      window.dispatchEvent(new CustomEvent(ACTION_CENTER_BRIDGE_ACTIVITY_EVENT, { detail: { employeeId: key, activity } }));
    }
    if (bridgeActivityTimersRef.current[key]) clearTimeout(bridgeActivityTimersRef.current[key]);
    if (!bridgeActiveEmployeeIdsRef.current[key]) {
      const next = { ...bridgeActiveEmployeeIdsRef.current, [key]: true };
      bridgeActiveEmployeeIdsRef.current = next;
      setBridgeActiveEmployeeIds(next);
    }
    if (activity) {
      const nextEmployeeActivity = { ...bridgeActivityByEmployeeIdRef.current, [key]: activity };
      bridgeActivityByEmployeeIdRef.current = nextEmployeeActivity;
      setBridgeActivityByEmployeeId(nextEmployeeActivity);
      if (runKey) {
        if (bridgeActivityRunTimersRef.current[runKey]) clearTimeout(bridgeActivityRunTimersRef.current[runKey]);
        const nextRunActivity = { ...bridgeActivityByRunIdRef.current, [runKey]: activity };
        bridgeActivityByRunIdRef.current = nextRunActivity;
        setBridgeActivityByRunId(nextRunActivity);
        if (!activity.plan || !activity.plan.length) {
          bridgeActivityRunTimersRef.current[runKey] = setTimeout(() => {
            delete bridgeActivityRunTimersRef.current[runKey];
            if (!bridgeActivityByRunIdRef.current[runKey]) return;
            const next = { ...bridgeActivityByRunIdRef.current };
            delete next[runKey];
            bridgeActivityByRunIdRef.current = next;
            setBridgeActivityByRunId(next);
          }, BRIDGE_ACTIVITY_PULSE_MS);
        }
      }
    }
    bridgeActivityTimersRef.current[key] = setTimeout(() => {
      delete bridgeActivityTimersRef.current[key];
      if (!bridgeActiveEmployeeIdsRef.current[key]) return;
      const next = { ...bridgeActiveEmployeeIdsRef.current };
      delete next[key];
      bridgeActiveEmployeeIdsRef.current = next;
      setBridgeActiveEmployeeIds(next);
      if (bridgeActivityByEmployeeIdRef.current[key]) {
        const nextActivity = { ...bridgeActivityByEmployeeIdRef.current };
        delete nextActivity[key];
        bridgeActivityByEmployeeIdRef.current = nextActivity;
        setBridgeActivityByEmployeeId(nextActivity);
      }
    }, BRIDGE_ACTIVITY_PULSE_MS);
  }, []);

  useEffect(() => { refreshSession(); }, [refreshSession]);
  useEffect(() => {
    if (!session) return;
    if (session.authenticated) {
      setLoading(true);
      reload();
      refreshActionCenterOverview();
    } else {
      setDb(null);
      setLoading(false);
      setActionCenterOverview({ agents: [], runs: [], taggedTickets: [], needsInputCount: 0 });
      setActionCenterNeedsCount(0);
      Object.values(bridgeActivityTimersRef.current).forEach((timerId) => clearTimeout(timerId));
      Object.values(bridgeActivityRunTimersRef.current).forEach((timerId) => clearTimeout(timerId));
      bridgeActivityTimersRef.current = {};
      bridgeActivityRunTimersRef.current = {};
      bridgeActiveEmployeeIdsRef.current = {};
      bridgeActivityByEmployeeIdRef.current = {};
      bridgeActivityByRunIdRef.current = {};
      bridgeSpeechByRunIdRef.current = {};
      setBridgeActiveEmployeeIds({});
      setBridgeActivityByEmployeeId({});
      setBridgeActivityByRunId({});
      setBridgeSpeechByRunId({});
    }
  }, [session, reload, refreshActionCenterOverview]);
  useEffect(() => () => {
    Object.values(bridgeActivityTimersRef.current).forEach((timerId) => clearTimeout(timerId));
    Object.values(bridgeActivityRunTimersRef.current).forEach((timerId) => clearTimeout(timerId));
    bridgeActivityTimersRef.current = {};
    bridgeActivityRunTimersRef.current = {};
  }, []);
  useEffect(() => {
    if (!session || !session.authenticated || !window.API.subscribeToChanges) return;
    let timer = null;
    let actionCenterTimer = null;
    const scheduleWorkspaceReload = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        reload();
      }, 100);
    };
    const scheduleActionCenterOverview = () => {
      if (actionCenterTimer) clearTimeout(actionCenterTimer);
      actionCenterTimer = setTimeout(() => {
        actionCenterTimer = null;
        refreshActionCenterOverview();
      }, 150);
    };
    const unsubscribe = window.API.subscribeToChanges((change) => {
      if (isActionCenterTransientActivity(change)) {
        let liveActivity = null;
        if (window.AiChatMergeProgressActivity) {
          const activityRunId = String(change.jobId || "").toLowerCase();
          liveActivity = window.AiChatMergeProgressActivity(actionCenterTransientActivitiesRef.current[activityRunId], change);
          actionCenterTransientActivitiesRef.current[activityRunId] = liveActivity;
        }
        if (change && change.aiEmployeeId) markBridgeEmployeeActive(change.aiEmployeeId, change, liveActivity);
        scheduleActionCenterOverview();
        return;
      }
      if (change && change.kind === 'tool-call' && change.aiEmployeeId) {
        markBridgeEmployeeActive(change.aiEmployeeId, change);
        if (isProjectTicketChange(change) || isProjectSketchChange(change) || isProjectContextFileChange(change)) scheduleWorkspaceReload();
        return;
      }
      if (change && change.kind === 'employee-activity' && change.aiEmployeeId) {
        markBridgeEmployeeActive(change.aiEmployeeId, change);
        if (isProjectTicketChange(change) || isProjectSketchChange(change) || isProjectContextFileChange(change)) scheduleWorkspaceReload();
        if (isActionCenterOverviewChange(change)) scheduleActionCenterOverview();
        return;
      }
      if (change && change.toolName === 'testing') return;
      if (isProjectTicketChange(change) || isProjectSketchChange(change) || isProjectContextFileChange(change)) {
        scheduleWorkspaceReload();
        return;
      }
      if (isActionCenterOverviewChange(change)) {
        scheduleActionCenterOverview();
        return;
      }
      if (viewRef.current === "actionCenter") return;
      scheduleWorkspaceReload();
    });
    return () => {
      if (timer) clearTimeout(timer);
      if (actionCenterTimer) clearTimeout(actionCenterTimer);
      unsubscribe();
    };
  }, [session, reload, refreshActionCenterOverview, markBridgeEmployeeActive]);
  useEffect(() => {
    const syncView = () => {
      setViewState(viewFromHash());
      setRouteHash(window.location.hash);
    };
    window.addEventListener("hashchange", syncView);
    return () => window.removeEventListener("hashchange", syncView);
  }, []);

  useEffect(() => {
    document.documentElement.style.setProperty("--primary", t.accent);
    document.documentElement.style.setProperty("--primary-strong", shade(t.accent, -18));
    document.documentElement.style.setProperty("--primary-soft", tint(t.accent, 0.9));
    document.documentElement.style.setProperty("--primary-tint", tint(t.accent, 0.955));
  }, [t.accent]);
  useEffect(() => { if (!toast) return; const id = setTimeout(() => setToast(null), 2200); return () => clearTimeout(id); }, [toast]);
  useEffect(() => { setQuery(""); }, [view]);
  useEffect(() => { if (view !== "actionCenter") setActionCenterNeedsCount(0); }, [view]);

  const setView = useCallback((nextView) => {
    setViewState(nextView);
    const nextHash = nextView === "dashboard" ? "" : `#${nextView}`;
    if (window.location.hash !== nextHash) {
      window.history.pushState(null, "", `${window.location.pathname}${window.location.search}${nextHash}`);
      window.dispatchEvent(new Event("hashchange"));
    }
  }, []);

  const flash = useCallback((msg) => setToast(msg), []);
  const fail = useCallback((e) => setError((e && e.message) || "Request failed."), []);
  const openDrawer = useCallback((type, editId, context = {}) => setDrawer({ type, editId, context }), []);
  const closeDrawer = useCallback(() => setDrawer(null), []);

  const overdueCount = useMemo(() => db ? db.invoices.filter((iv) => MODEL.enrich(db, iv).overdue).length : 0, [db]);

  const refreshUnreadInboxCount = useCallback(async () => {
    if (!db || !db.settings || !db.settings.emailBackendConfigured) {
      setUnreadInboxCount(0);
      return;
    }

    try {
      const result = await window.API.getEmailUnreadCount();
      setUnreadInboxCount(Number(result.count) || 0);
    } catch (_) {
      setUnreadInboxCount(0);
    }
  }, [db]);

  useEffect(() => { refreshUnreadInboxCount(); }, [refreshUnreadInboxCount]);

  function patchProjectGuidelineState(projectId, workPackageId, field, guideline, remove = false) {
    const patchList = (items = []) => {
      if (remove) return items.filter((item) => item.id !== guideline.id);
      const exists = items.some((item) => item.id === guideline.id);
      return exists
        ? items.map((item) => item.id === guideline.id ? guideline : item)
        : [...items, guideline];
    };
    setDb((current) => current ? {
      ...current,
      projects: (current.projects || []).map((project) => {
        if (project.id !== projectId) return project;
        if (!workPackageId) return { ...project, [field]: patchList(project[field] || []) };
        return {
          ...project,
          workPackages: (project.workPackages || []).map((workPackage) => workPackage.id === workPackageId
            ? { ...workPackage, [field]: patchList(workPackage[field] || []) }
            : workPackage),
        };
      }),
    } : current);
  }

  async function handleInvoiceAction(action, iv) {
    try {
      if (action === "print") { window.open(`/invoices/${iv.id}/print`, "_blank", "noreferrer"); return true; }
      if (action === "download") { window.open(`/invoices/${iv.id}/download`, "_blank"); return true; }
      if (action === "downloadProjectOffer") { window.open(`/projects/${iv.projectId}/offers/${iv.id}/download`, "_blank"); return true; }
      if (action === "doc") { window.open(`/api/expenses/${iv.id}/document`, "_blank", "noreferrer"); return true; }
      if (action === "openProjectDocument") { window.open(`/api/projects/${iv.projectId}/documents/${iv.documentId}`, "_blank", "noreferrer"); return true; }
      if (action === "startProjectCodexOpenTickets") {
        const result = await window.API.startProjectCodexOpenTickets(iv.projectId);
        if (result.appUrl) window.open(result.appUrl, "_blank", "noreferrer");
        flash(result.appUrl ? "Codex thread started" : "Codex prompt queued");
        return true;
      }
      if (action === "uploadProjectDocument") {
        const document = await window.API.uploadProjectDocument(iv.projectId, iv.file, iv);
        flash("Project document uploaded");
        await reload();
        return document;
      }
      if (action === "uploadProjectDocumentPackage") {
        const result = await window.API.uploadProjectDocumentPackage(iv.projectId, iv.files, iv);
        flash("Project package uploaded");
        await reload();
        return result;
      }
      if (action === "attachExternalProjectDocument") {
        const document = await window.API.attachExternalProjectDocument(iv.projectId, iv);
        flash("External document attached");
        await reload();
        return document;
      }
      if (action === "syncExternalProjectDocument") {
        const document = await window.API.syncExternalProjectDocument(iv.projectId, iv.documentId);
        flash(document.externalSyncStatus === "ok" ? "External document synced" : "External document sync checked");
        await reload();
        return document;
      }
      if (action === "importStorageBaseFolderDocuments") {
        const result = await window.API.importStorageBaseFolderDocuments(iv.storageConnectionId);
        flash(`Imported ${result.created || 0} new, updated ${result.updated || 0} unlinked`);
        await reload();
        return result;
      }
      if (action === "linkStorageDocumentToProject") {
        const document = await window.API.linkStorageDocumentToProject(iv.storageDocumentId, iv);
        flash("Document linked to project");
        await reload();
        return document;
      }
      if (action === "attachExistingProjectDocument") {
        const document = await window.API.attachExistingProjectDocument(iv.projectId, iv.documentId, iv);
        flash("Document linked to project");
        await reload();
        return document;
      }
      if (action === "unlinkStorageDocumentFromProject") {
        const document = await window.API.unlinkStorageDocumentFromProject(iv.storageDocumentId);
        flash("Document unlinked from project");
        await reload();
        return document;
      }
      if (action === "deleteProjectDocument") {
        if (!window.confirm(`Remove ${iv.fileName}?`)) return;
        await window.API.deleteProjectDocument(iv.projectId, iv.documentId);
        flash("Project document removed");
        await reload();
        return true;
      }
      if (action === "updateProjectDocumentRequirement") {
        await window.API.updateProjectDocumentRequirement(iv.projectId, iv.documentId, iv.isRequirement);
        flash(iv.isRequirement ? "Document marked as scope source" : "Document unmarked as scope source");
      }
      if (action === "updateProjectDocumentDescription") {
        await window.API.updateProjectDocumentDescription(iv.projectId, iv.documentId, iv.description);
        flash("Document description updated");
      }
      if (action === "createProjectNote") {
        await window.API.createNote({ ...iv.note, projectId: iv.projectId });
        flash("Project note added");
      }
      if (action === "updateProjectNote") {
        await window.API.updateNote(iv.noteId, { ...iv.note, projectId: iv.projectId });
        flash("Project note updated");
      }
      if (action === "updateProjectContextMilestones") {
        await window.API.updateProjectContextMilestones(iv.projectId, iv.type, iv.itemId, iv.milestoneIds);
        flash("Context routes updated");
      }
      if (action === "deleteProjectNote") {
        if (!window.confirm(`Delete ${iv.subject || 'this note'}?`)) return false;
        await window.API.deleteNote(iv.id);
        flash("Project note deleted");
      }
      if (action === "updateProjectMeetingFollowup") {
        await window.API.updateCalendarEventFollowup(iv.eventId, iv.followupId, iv.followup, iv.projectId);
        flash("Follow-up updated");
      }
      if (action === "deleteProjectMeetingFollowup") {
        if (!window.confirm(`Delete ${iv.text || 'this follow-up'}?`)) return false;
        await window.API.deleteCalendarEventFollowup(iv.eventId, iv.followupId, iv.projectId);
        flash("Follow-up deleted");
      }
      if (action === "updateProjectEmailStatus") {
        await window.API.updateProjectEmailStatus(iv.projectId, iv.emailId, { status: iv.status || "unprocessed", isRequirement: iv.isRequirement });
        flash("Project email updated");
      }
      if (action === "deleteProjectEmail") {
        if (!window.confirm(`Unlink ${iv.subject || 'this email'} from project?`)) return false;
        await window.API.deleteProjectEmail(iv.projectId, iv.emailId);
        flash("Project email unlinked");
      }
      if (action === "updateProjectPlanning") {
        await window.API.updateProjectPlanning(iv.projectId, iv);
        flash("Project planning updated");
      }
      if (action === "createProjectWorkPackage") {
        await window.API.createProjectWorkPackage(iv.projectId, iv.workPackage);
        flash("WorkPackage created");
      }
      if (action === "deleteProjectWorkPackage") {
        if (!window.confirm(`Delete ${iv.name || 'this workPackage'}?`)) return false;
        await window.API.deleteProjectWorkPackage(iv.projectId, iv.workPackageId);
        flash("WorkPackage deleted");
      }
      if (action === "updateWorkPackagePhase") {
        await window.API.updateWorkPackagePhase(iv.projectId, iv.workPackageId, iv.phase);
        flash(`WorkPackage phase set to ${iv.phase}`);
      }
      if (action === "startWorkPackageCodexPrompt") {
        const result = await window.API.startWorkPackageCodexPrompt(iv.projectId, iv.workPackageId, iv.kind);
        if (result.appUrl) window.open(result.appUrl, "_blank", "noreferrer");
        flash(result.appUrl ? "Codex thread started" : "Codex prompt queued");
        return true;
      }
      if (action === "createProjectSketch") {
        await window.API.createProjectSketch(iv.projectId, iv.sketch);
        flash("Sketch created");
      }
      if (action === "updateProjectSketch") {
        await window.API.updateProjectSketch(iv.projectId, iv.sketchId, iv.sketch);
        flash(iv.silent ? "Sketch saved" : "Sketch updated");
      }
      if (action === "saveProjectSketchStandaloneExport") {
        const document = await window.API.saveProjectSketchStandaloneExport(iv.projectId, iv.sketchId, iv.export);
        flash("Sketch export saved to files");
        await reload();
        return document;
      }
      if (action === "deleteProjectSketch") {
        if (!window.confirm(`Delete ${iv.title || 'this sketch'}?`)) return false;
        await window.API.deleteProjectSketch(iv.projectId, iv.sketchId);
        flash("Sketch deleted");
      }
      if (action === "reorderProjectSketch") {
        const allSketches = iv.project.sketches || [];
        const current = allSketches.find((item) => item.id === iv.sketchId);
        if (!current) return false;
        const sketches = allSketches
          .filter((item) => item.workPackageId === current.workPackageId)
          .slice()
          .sort((a, b) => (Number(a.position) || 0) - (Number(b.position) || 0) || String(a.title || '').localeCompare(String(b.title || '')));
        const from = sketches.findIndex((item) => item.id === iv.sketchId);
        const to = from + iv.direction;
        if (from < 0 || to < 0 || to >= sketches.length) return false;
        const ordered = sketches.map((item) => item.id);
        [ordered[from], ordered[to]] = [ordered[to], ordered[from]];
        await window.API.reorderProjectSketches(iv.project.id, ordered);
        flash("Sketches reordered");
      }
      if (action === "createProjectRequirement") {
        await window.API.createProjectRequirement(iv.projectId, iv.requirement);
        flash("Scope concept created");
      }
      if (action === "updateProjectRequirement") {
        await window.API.updateProjectRequirement(iv.projectId, iv.requirementId, iv.requirement);
        flash("Scope concept updated");
      }
      if (action === "deleteProjectRequirement") {
        if (!window.confirm(`Delete ${iv.title || 'this scope concept'}?`)) return false;
        await window.API.deleteProjectRequirement(iv.projectId, iv.requirementId, iv.workPackageId);
        flash("Scope concept deleted");
      }
      if (action === "createProjectSpec") {
        const spec = await window.API.createProjectSpec(iv.projectId, iv.spec || {});
        flash("Spec generated");
        await reload();
        return spec;
      }
      if (action === "updateProjectSpec") {
        const spec = await window.API.updateProjectSpec(iv.projectId, iv.specId, iv.spec || {});
        flash("Spec updated");
        await reload();
        return spec;
      }
      if (action === "deleteProjectSpec") {
        if (!window.confirm(`Delete ${iv.title || 'this spec'}?`)) return false;
        await window.API.deleteProjectSpec(iv.projectId, iv.specId);
        flash("Spec deleted");
        await reload();
        return true;
      }
      if (action === "approveProjectSpec") {
        const spec = await window.API.approveProjectSpec(iv.projectId, iv.specId);
        flash("Spec approved");
        await reload();
        return spec;
      }
      if (action === "refreshProjectSpecSnapshot") {
        const spec = await window.API.refreshProjectSpecSnapshot(iv.projectId, iv.specId);
        flash("Source snapshot refreshed");
        await reload();
        return spec;
      }
      if (action === "createProjectSpecTicketPlan") {
        const pack = await window.API.getProjectSpecAgentPack(iv.projectId, iv.specId);
        flash("Ticket plan source prepared");
        return pack;
      }
      if (action === "updateProjectKnowledgeConcept") {
        await window.API.updateProjectKnowledgeConcept(iv.projectId, iv.conceptId, iv.concept);
        flash("OKF concept updated");
      }
      if (action === "deleteProjectKnowledgeConcept") {
        if (!window.confirm(`Delete ${iv.title || 'this OKF concept'}?`)) return false;
        await window.API.deleteProjectKnowledgeConcept(iv.projectId, iv.conceptId);
        flash("OKF concept deleted");
      }
      if (action === "updateProjectWorkPackageDesignGuidelineTemplates") {
        if (iv.title && !window.confirm(`Remove ${iv.title || 'this design guideline template'} from this workPackage?`)) return false;
        const workPackage = await window.API.updateProjectWorkPackageDesignGuidelineTemplates(iv.projectId, iv.workPackageId, iv.templateIds);
        setDb((current) => current ? {
          ...current,
          projects: (current.projects || []).map((project) => project.id === iv.projectId
            ? {
              ...project,
              workPackages: (project.workPackages || []).map((item) => item.id === iv.workPackageId
                ? { ...item, designGuidelineTemplateIds: workPackage.designGuidelineTemplateIds || [] }
                : item),
            }
            : project),
        } : current);
        flash("Design guidelines updated");
        return true;
      }
      if (action === "updateProjectWorkPackageImplementationGuidelineTemplates") {
        if (iv.title && !window.confirm(`Remove ${iv.title || 'this implementation guideline template'} from this workPackage?`)) return false;
        const workPackage = await window.API.updateProjectWorkPackageImplementationGuidelineTemplates(iv.projectId, iv.workPackageId, iv.templateIds);
        setDb((current) => current ? {
          ...current,
          projects: (current.projects || []).map((project) => project.id === iv.projectId
            ? {
              ...project,
              workPackages: (project.workPackages || []).map((item) => item.id === iv.workPackageId
                ? { ...item, implementationGuidelineTemplateIds: workPackage.implementationGuidelineTemplateIds || [] }
                : item),
            }
            : project),
        } : current);
        flash("Implementation guidelines updated");
        return true;
      }
      if (action === "updateProjectWorkPackageExcalidrawLibraryTemplates") {
        if (iv.title && !window.confirm(`Remove ${iv.title || 'this sketch library template'} from this workPackage?`)) return false;
        const workPackage = await window.API.updateProjectWorkPackageExcalidrawLibraryTemplates(iv.projectId, iv.workPackageId, iv.templateIds);
        setDb((current) => current ? {
          ...current,
          projects: (current.projects || []).map((project) => project.id === iv.projectId
            ? {
              ...project,
              workPackages: (project.workPackages || []).map((item) => item.id === iv.workPackageId
                ? { ...item, excalidrawLibraryTemplateIds: workPackage.excalidrawLibraryTemplateIds || [] }
                : item),
            }
            : project),
        } : current);
        flash("Sketch libraries updated");
        return true;
      }
      if (action === "createProjectGuideline") {
        const api = iv.kind === "design"
          ? window.API.createProjectDesignGuideline
          : window.API.createProjectImplementationGuideline;
        const field = iv.kind === "design" ? "designGuidelines" : "implementationGuidelines";
        const guideline = await api(iv.projectId, iv.guideline);
        patchProjectGuidelineState(iv.projectId, null, field, guideline);
        flash("Guideline created");
        return true;
      }
      if (action === "updateProjectGuideline") {
        const api = iv.kind === "design"
          ? window.API.updateProjectDesignGuideline
          : window.API.updateProjectImplementationGuideline;
        const field = iv.kind === "design" ? "designGuidelines" : "implementationGuidelines";
        const guideline = await api(iv.projectId, iv.guidelineId, iv.guideline);
        patchProjectGuidelineState(iv.projectId, null, field, guideline);
        flash("Guideline updated");
        return true;
      }
      if (action === "deleteProjectGuideline") {
        if (!window.confirm(`Delete ${iv.title || 'this guideline'}?`)) return false;
        const api = iv.kind === "design"
          ? window.API.deleteProjectDesignGuideline
          : window.API.deleteProjectImplementationGuideline;
        const field = iv.kind === "design" ? "designGuidelines" : "implementationGuidelines";
        await api(iv.projectId, iv.guidelineId);
        patchProjectGuidelineState(iv.projectId, null, field, { id: iv.guidelineId }, true);
        flash("Guideline deleted");
        return true;
      }
      if (action === "createProjectWorkPackageGuideline") {
        const api = iv.kind === "design"
          ? window.API.createProjectWorkPackageDesignGuideline
          : window.API.createProjectWorkPackageImplementationGuideline;
        const field = iv.kind === "design" ? "designGuidelines" : "implementationGuidelines";
        const guideline = await api(iv.projectId, iv.workPackageId, iv.guideline);
        patchProjectGuidelineState(iv.projectId, iv.workPackageId, field, guideline);
        flash("Guideline created");
        return true;
      }
      if (action === "updateProjectWorkPackageGuideline") {
        const api = iv.kind === "design"
          ? window.API.updateProjectWorkPackageDesignGuideline
          : window.API.updateProjectWorkPackageImplementationGuideline;
        const field = iv.kind === "design" ? "designGuidelines" : "implementationGuidelines";
        const guideline = await api(iv.projectId, iv.workPackageId, iv.guidelineId, iv.guideline);
        patchProjectGuidelineState(iv.projectId, iv.workPackageId, field, guideline);
        flash("Guideline updated");
        return true;
      }
      if (action === "deleteProjectWorkPackageGuideline") {
        if (!window.confirm(`Delete ${iv.title || 'this guideline'}?`)) return false;
        const api = iv.kind === "design"
          ? window.API.deleteProjectWorkPackageDesignGuideline
          : window.API.deleteProjectWorkPackageImplementationGuideline;
        const field = iv.kind === "design" ? "designGuidelines" : "implementationGuidelines";
        await api(iv.projectId, iv.workPackageId, iv.guidelineId);
        patchProjectGuidelineState(iv.projectId, iv.workPackageId, field, { id: iv.guidelineId }, true);
        flash("Guideline deleted");
        return true;
      }
      if (action === "createProjectTechStackItem") {
        await window.API.createProjectTechStackItem(iv.projectId, iv.item);
        flash("Tech stack item created");
      }
      if (action === "updateProjectTechStackItem") {
        await window.API.updateProjectTechStackItem(iv.projectId, iv.itemId, iv.item);
        flash("Tech stack item updated");
      }
      if (action === "deleteProjectTechStackItem") {
        if (!window.confirm(`Delete ${iv.tech || 'this tech stack item'}?`)) return false;
        await window.API.deleteProjectTechStackItem(iv.projectId, iv.itemId);
        flash("Tech stack item deleted");
      }
      if (action === "updateProjectGuidelineTemplates") {
        const project = await window.API.updateProjectGuidelineTemplates(iv.projectId, iv.templateIds);
        setDb((current) => current ? {
          ...current,
          projects: (current.projects || []).map((item) => item.id === iv.projectId
            ? { ...item, implementationGuidelineTemplateIds: project.implementationGuidelineTemplateIds || [] }
            : item),
        } : current);
        flash("Guidelines updated");
        return true;
      }
      if (action === "updateProjectDesignGuidelineTemplates") {
        const project = await window.API.updateProjectDesignGuidelineTemplates(iv.projectId, iv.templateIds);
        setDb((current) => current ? {
          ...current,
          projects: (current.projects || []).map((item) => item.id === iv.projectId
            ? { ...item, designGuidelineTemplateIds: project.designGuidelineTemplateIds || [] }
            : item),
        } : current);
        flash("Design guidelines updated");
        return true;
      }
      if (action === "updateProjectExcalidrawLibraryTemplates") {
        const project = await window.API.updateProjectExcalidrawLibraryTemplates(iv.projectId, iv.templateIds);
        setDb((current) => current ? {
          ...current,
          projects: (current.projects || []).map((item) => item.id === iv.projectId
            ? { ...item, excalidrawLibraryTemplateIds: project.excalidrawLibraryTemplateIds || [] }
            : item),
        } : current);
        flash("Sketch libraries updated");
        return true;
      }
      if (action === "updateProject") {
        await window.API.updateProject(iv.projectId, { ...iv.project, ...iv.patch });
        flash("Project updated");
      }
      if (action === "toggleProjectMilestone") {
        const milestone = (iv.project.milestones || []).find((item) => item.id === iv.milestoneId);
        if (!milestone) return false;
        await window.API.updateProjectMilestone(iv.project.id, milestone.id, { ...milestone, completed: !milestone.completed });
        flash("Milestone updated");
      }
      if (action === "reorderProjectMilestone") {
        const allMilestones = iv.project.milestones || [];
        const current = allMilestones.find((item) => item.id === iv.milestoneId);
        if (!current) return false;
        const milestones = allMilestones.filter((item) => item.workPackageId === current.workPackageId);
        const from = milestones.findIndex((item) => item.id === iv.milestoneId);
        const to = from + iv.direction;
        if (from < 0 || to < 0 || to >= milestones.length) return false;
        const ordered = milestones.map((item) => item.id);
        [ordered[from], ordered[to]] = [ordered[to], ordered[from]];
        await window.API.reorderProjectMilestones(iv.project.id, ordered);
        flash("Milestones reordered");
      }
      if (action === "createMilestoneDeliverables") {
        for (const milestone of iv.milestones || []) {
          const unitPrice = milestoneInvoiceAmount(milestone);
          await window.API.createProjectDeliverable(iv.project.id, {
            projectId: iv.project.id,
            billingStartDate: milestone.dueDate || window.AC.TODAY,
            milestoneIds: [milestone.id],
            name: milestone.title,
            kind: "Milestone",
            status: "Active",
            description: milestone.dueDate ? `Due ${milestone.dueDate}` : "",
            unitPrice,
            taxRate: 0.19,
            freeOfCharge: unitPrice <= 0,
            recurrenceFrequency: "None",
            recurrenceEndDate: "",
            customFields: {},
          });
        }
        flash(`${(iv.milestones || []).length} deliverable${(iv.milestones || []).length !== 1 ? 's' : ''} created`);
      }
      if (action === "removeDoc") {
        if (!window.confirm("Remove this document from the expense?")) return;
        await window.API.deleteExpenseDocument(iv.id);
        flash("Expense document removed");
      }
      if (action === "deleteExpense") {
        if (!window.confirm(`Delete ${iv.category} expense?`)) return;
        await window.API.deleteExpense(iv.id);
        flash("Expense deleted");
      }
      if (action === "deleteEmployee") {
        if (!window.confirm(`Delete ${iv.name}? Linked users and expenses will be detached.`)) return;
        await window.API.deleteEmployee(iv.id);
        flash("Employee deleted");
      }
      if (action === "payExpenseOccurrence") {
        await window.API.payExpenseOccurrence(iv.expense.id, iv.occurrenceDate);
        flash("Expense payment recorded");
      }
      if (action === "finalize") { await window.API.finalizeInvoice(iv.id); flash(`${iv.number} finalized`); }
      if (action === "pay") { await window.API.payInvoice(iv.id, MODEL.totals(iv).outstanding); flash(`${iv.number} marked paid`); }
      if (action === "cancel") { await window.API.cancelInvoice(iv.id); flash(`${iv.number} cancelled`); }
      if (action === "delete") {
        if (!window.confirm(`Delete ${iv.number}?`)) return;
        await window.API.deleteInvoice(iv.id);
        flash(`${iv.number} deleted`);
      }
      if (action === "deleteProjectDeliverable") {
        if (!window.confirm(`Delete ${iv.name}?`)) return;
        await window.API.deleteProjectDeliverable(iv.projectId, iv.id);
        flash("Deliverable deleted");
      }
      if (action === "unlinkProjectCustomer") {
        if (!window.confirm(`Remove ${iv.customerName} from this project?`)) return;
        await window.API.unlinkProjectCustomer(iv.projectId, iv.customerId);
        flash("Customer removed from project");
      }
      if (action === "deleteCustomerContact") {
        if (!window.confirm(`Remove ${iv.contactName}?`)) return;
        await window.API.deleteCustomerContact(iv.customerId, iv.contactId);
        flash("Contact removed");
      }
      if (action === "deleteNote") {
        if (!window.confirm(`Delete ${iv.subject || 'this note'}?`)) return;
        await window.API.deleteNote(iv.id);
        flash("Note deleted");
      }
      if (action === "deleteProjectMilestone") {
        if (!window.confirm(`Delete ${iv.title}?`)) return;
        await window.API.deleteProjectMilestone(iv.projectId, iv.id);
        flash("Milestone deleted");
      }
      if (action === "deleteProject") {
        if (!window.confirm(`Delete ${iv.name}? Draft invoices for this project will also be removed.`)) return;
        await window.API.deleteProject(iv.id);
        flash("Project deleted");
      }
      if (action === "deleteProjectOffer") {
        if (!window.confirm(`Delete ${iv.title}?`)) return;
        await window.API.deleteProjectOffer(iv.projectId, iv.id);
        flash("Offer deleted");
      }
      if (action === "convertProjectOffer") {
        if (!window.confirm(`Convert ${iv.title} to project deliverable${(iv.lines || []).length !== 1 ? 's' : ''}?`)) return;
        await window.API.convertProjectOffer(iv.projectId, iv.id);
        flash("Offer converted");
      }
      await reload();
      return true;
    } catch (e) { fail(e); return false; }
  }

  async function handleSubmit(type, f, editId) {
    try {
      if (type === "invoice") {
        editId ? await window.API.updateInvoice(editId, f) : await window.API.createInvoice(f);
        flash(editId ? "Invoice updated" : "Draft invoice created");
      } else if (type === "expense") {
        const saved = editId ? await window.API.updateExpense(editId, f) : await window.API.createExpense(f);
        const file = document.querySelector('.drawer input[type=file]');
        if (file && file.files && file.files[0]) await window.API.uploadExpenseDocument(saved.id, file.files[0]);
        flash(editId ? "Expense updated" : "Expense recorded");
      } else if (type === "customer") {
        editId ? await window.API.updateCustomer(editId, f) : await window.API.createCustomer(f);
        flash(editId ? "Customer updated" : "Customer added");
      } else if (type === "project") {
        if (editId) {
          const existing = db.projects.find((p) => p.id === editId);
          await window.API.updateProject(editId, { ...existing, ...f, customFields: existing.customFields || {}, customerIds: existing.customerIds || [] });
          flash("Project updated");
        } else {
          await window.API.createProject({ ...f, customFields: {}, customerIds: f.customerId ? [f.customerId] : [] });
          flash("Project created");
        }
      } else if (type === "projectCustomer") {
        await window.API.linkProjectCustomer(f.projectId, f.customerId);
        flash("Customer linked");
      } else if (type === "projectMilestone") {
        if (editId) {
          const project = db.projects.find((item) => item.id === f.projectId);
          const existing = project && (project.milestones || []).find((item) => item.id === editId);
          const onlyEffortChanged = existing
            && (existing.title || '') === (f.title || '')
            && (existing.dueDate || '') === (f.dueDate || '')
            && !!existing.completed === !!f.completed;
          onlyEffortChanged
            ? await window.API.updateProjectMilestoneEffort(f.projectId, editId, f)
            : await window.API.updateProjectMilestone(f.projectId, editId, f);
        } else {
          await window.API.createProjectMilestone(f.projectId, f);
        }
        flash(editId ? "Milestone updated" : "Milestone created");
      } else if (type === "projectDeliverable") {
        editId ? await window.API.updateProjectDeliverable(f.projectId, editId, f) : await window.API.createProjectDeliverable(f.projectId, f);
        flash(editId ? "Deliverable updated" : "Deliverable created");
      } else if (type === "deliverableInvoice") {
        const invoices = await window.API.createProjectDeliverableInvoices(f.projectId, f.deliverableId, f);
        flash(invoices.length ? `${invoices.length} invoice draft${invoices.length !== 1 ? 's' : ''} created` : "No missing invoices");
      } else if (type === "projectOffer") {
        editId ? await window.API.updateProjectOffer(f.projectId, editId, f) : await window.API.createProjectOffer(f.projectId, f);
        flash(editId ? "Offer updated" : "Offer created");
      } else if (type === "employee") {
        editId ? await window.API.updateEmployee(editId, f) : await window.API.createEmployee(f);
        flash(editId ? "Employee updated" : "Employee added");
      } else if (type === "note") {
        if (editId) {
          await window.API.updateNote(editId, f);
        } else {
          await window.API.createNote(f);
        }
        flash(editId ? "Note updated" : "Note added");
      } else if (type === "customerContact") {
        editId
          ? await window.API.updateCustomerContact(f.customerId, editId, f)
          : await window.API.createCustomerContact(f.customerId, f);
        flash(editId ? "Contact updated" : "Contact added");
      }
      closeDrawer();
      await reload();
    } catch (e) { fail(e); }
  }

  async function handleSettings(next) {
    try { await window.API.updateSettings({ ...next, nextInvoiceNumber: parseInt(next.nextInvoiceNumber || "1", 10) || 1 }); flash("Settings saved"); await reload(); }
    catch (e) { fail(e); }
  }

  async function handleAuth(form) {
    try {
      const next = await window.API.login(form);
      setSession(next);
      setError(null);
    } catch (e) { fail(e); }
  }

  async function handleLogout() {
    try {
      await window.API.logout();
      setSession({ authenticated: false, user: null });
      setDb(null);
      flash("Signed out");
    } catch (e) { fail(e); }
  }

  const openActionCenterRun = useCallback((employeeId, runId) => {
    if (runId) {
      const activityRunId = String(runId || "").toLowerCase();
      setChatWindow({ type: "action-center", employeeId: employeeId || "", runId, initialLiveActivity: actionCenterTransientActivitiesRef.current[activityRunId] || null });
    }
    const nextHash = actionCenterThreadHash(employeeId, runId);
    setViewState("actionCenter");
    if (window.location.hash !== nextHash) {
      window.history.pushState(null, "", `${window.location.pathname}${window.location.search}${nextHash}`);
    }
    window.dispatchEvent(new Event("hashchange"));
  }, []);

  if (authLoading || (session && session.authenticated && loading)) return <div className="boot"><div className="boot-mark">A</div><span>Loading workspace…</span></div>;
  if (session && !session.authenticated) return <LandingPage error={error} onSubmit={handleAuth} />;
  if (!db) return <div className="boot"><div className="alert" style={{ maxWidth: 420 }}>{error || "No data."}</div><button className="btn" onClick={reload}>Retry</button></div>;

  const meta = VIEW_META[view];
  const section = (NAV.find((g) => g.items.some((i) => i.id === view)) || { group: "Workspace" }).group;
  const activeProjectId = view === "projects" ? projectIdFromHashForShell(routeHash) : null;
  const VoiceAssistantButton = window.VoiceAssistantButton;
  const VoiceAssistantPanel = window.VoiceAssistantPanel;
  const AppligatorFactoryGraphics = window.AppligatorFactoryGraphics;
  const activeProject = activeProjectId ? db.projects.find((p) => p.id === activeProjectId) : null;
  const isProjectDetail = !!activeProject;
  const isFlushView = isProjectDetail || view === "actionCenter" || view === "appligatorFactory" || view === "appligatorFactoryGraphics" || view === "appligatorOffice";
  const topbarTitle = activeProject ? activeProject.name : meta[0];
  const currentUser = session && session.user;
  const currentOrganization = session && session.organization;
  const currentEmployeeId = currentUser && currentUser.employeeId;
  const screenProps = { db, query, routeHash, actionCenterRoute, actionCenterOverview, bridgeActiveEmployeeIds, bridgeActivityByEmployeeId, bridgeActivityByRunId, bridgeSpeechByRunId, currentEmployeeId, onDrawer: openDrawer, onAction: handleInvoiceAction, onNav: setView, onOpenActionCenterRun: openActionCenterRun, onToast: flash, onError: fail, onReload: reload, onUnreadRefresh: refreshUnreadInboxCount, onVoiceContextChange: setVoicePageContext };
  const sidebarTone = t.sidebar === "light" ? "light" : "dark";
  const ChatModal = window.ActionCenterRunChatModal;
  const actionCenterReviewCount = readyForReviewThreadCount(db);

  function navTooltip(item) {
    const counts = [];
    if (item.id === "actionCenter" && actionCenterReviewCount > 0) counts.push(`${actionCenterReviewCount} thread${actionCenterReviewCount === 1 ? "" : "s"} ready for review`);
    if (item.id === "email" && unreadInboxCount > 0) counts.push(`${unreadInboxCount} unread email${unreadInboxCount === 1 ? "" : "s"}`);
    if (item.id === "invoices" && overdueCount > 0) counts.push(`${overdueCount} overdue`);
    return counts.length ? `${item.label} - ${counts.join(", ")}` : item.label;
  }

  return (
    <div className="app" data-sidebar={sidebarTone} data-nav-state={sidebarExpanded ? "expanded" : "collapsed"} data-density={t.density}>
      <aside className="sidebar">
        <div className="brand" title={sidebarExpanded ? undefined : db.settings.companyName}>
          <span className="mark"><BrandMark /></span>
          <div><strong>{db.settings.companyName}</strong><small>{currentOrganization ? currentOrganization.name : "Operations"}</small></div>
        </div>
        {NAV.map((g) => (
          <div className="nav-group" key={g.group}>
            <div className="nav-label">{g.group}</div>
            {g.items.map((it) => (
              <button key={it.id} className={`nav-btn ${view === it.id ? "active" : ""}`} title={navTooltip(it)} data-tooltip={navTooltip(it)} aria-label={navTooltip(it)} onClick={() => setView(it.id)}>
                <span className="nav-icon-wrap">
                  <Icon name={it.icon} size={15} className="ico" />
                  {it.id === "actionCenter" && actionCenterReviewCount > 0 && <span className="icon-badge" aria-hidden="true">{actionCenterReviewCount}</span>}
                </span>
                <span className="label">{it.label}</span>
                {it.id === "email" && unreadInboxCount > 0 && <span className="badge" title={`${unreadInboxCount} unread inbox email${unreadInboxCount === 1 ? "" : "s"}`}>{unreadInboxCount}</span>}
                {it.id === "invoices" && overdueCount > 0 && <span className="badge">{overdueCount}</span>}
              </button>
            ))}
          </div>
        ))}
        <div className="nav-group">
          <button className={`nav-btn ${view === "settings" ? "active" : ""}`} title="Settings" data-tooltip="Settings" aria-label="Settings" onClick={() => setView("settings")}>
            <Icon name="settings" size={15} className="ico" /><span className="label">Settings</span>
          </button>
        </div>
        <div className="sb-foot">
          <div className="who">
            <span className="avatar">{((currentUser && currentUser.name) || db.settings.companyName || "A").slice(0, 2).toUpperCase()}</span>
            <div className="meta"><div style={{ color: "var(--sb-text-strong)", fontWeight: 600, fontSize: 12 }}>{(currentUser && currentUser.name) || db.settings.companyName}</div><div style={{ fontSize: 10.5 }}>{currentUser ? currentUser.role : "Operations"}</div></div>
          </div>
          <button type="button" className="sidebar-toggle" title={sidebarExpanded ? "Collapse navigation" : "Expand navigation"} aria-label={sidebarExpanded ? "Collapse navigation" : "Expand navigation"} aria-expanded={sidebarExpanded} onClick={() => setSidebarExpanded((expanded) => !expanded)}>
            <Icon name={sidebarExpanded ? "chevL" : "chevR"} size={16} />
          </button>
        </div>
      </aside>

      <main className="main">
        <header className="topbar">
          <div className="topbar-title">
            {activeProject ? (
              <div className="topbar-breadcrumbs" aria-label="Breadcrumb">
                <span>{section}</span>
                <span>/</span>
                <button type="button" onClick={() => setView("projects")}>Projects</button>
                <span>/</span>
                <span>{activeProject.kind || "Project"}</span>
              </div>
            ) : (
              <div className="crumb">{view === "settings" ? "Settings" : section}</div>
            )}
            <h1>{topbarTitle}</h1>
          </div>
          <div className="spacer" />
          {view === "actionCenter" && actionCenterNeedsCount > 0 && <span className="ops-today ac-top-need"><Icon name="bell" size={14} />{actionCenterNeedsCount} need you</span>}
          <TopbarAiEmployeeActivity overview={actionCenterOverview} bridgeActiveEmployeeIds={bridgeActiveEmployeeIds} bridgeActivityByEmployeeId={bridgeActivityByEmployeeId} onOpenRun={openActionCenterRun} />
          <VoiceAssistantButton open={voiceAssistantOpen} onToggle={() => setVoiceAssistantOpen((open) => !open)} />
          {["actionCenter", "implementationGuidelines", "documents", "invoices", "expenses", "payments", "customers", "projects", "employees", "email"].includes(view) &&
            <SearchBox value={query} onChange={setQuery} placeholder={`Search ${meta[0].toLowerCase()}…`} />}
          {currentOrganization && <span className="ops-today" title={`Organization: ${currentOrganization.slug}`}><Icon name="customers" size={14} />{currentOrganization.name}</span>}
          <button className="icon-btn" title="Refresh" onClick={() => { setLoading(true); reload().then(() => flash("Data refreshed")); }}><Icon name="refresh" size={16} /></button>
          <button className="icon-btn" title="Sign out" onClick={handleLogout}><Icon name="logout" size={16} /></button>
        </header>

        {error && <div className="alert" style={{ margin: "12px 20px 0" }}>{error}</div>}

        <div className={'content' + (isFlushView ? ' flush' : '')}>
          {view === "dashboard" && <Dashboard db={db} year={year} setYear={setYear} onNav={setView} />}
          {view === "actionCenter" && (
            <ActionCenter
              db={db}
              query={query}
              actionCenterRoute={actionCenterRoute}
              onNav={setView}
              onOpenActionCenterRun={openActionCenterRun}
              onToast={flash}
              onError={fail}
              onNeedsInputCount={setActionCenterNeedsCount}
              onOverviewChange={setActionCenterOverview}
              bridgeActivityByRunId={bridgeActivityByRunId}
              bridgeSpeechByRunId={bridgeSpeechByRunId}
            />
          )}
          {view === "appligatorFactory" && (
            <AppligatorFactory
              db={db}
              onToast={flash}
              onError={fail}
              bridgeActivityByRunId={bridgeActivityByRunId}
              bridgeSpeechByRunId={bridgeSpeechByRunId}
            />
          )}
          {view === "appligatorFactoryGraphics" && (
            AppligatorFactoryGraphics ? (
              <AppligatorFactoryGraphics
                db={db}
                onToast={flash}
                onError={fail}
                bridgeActivityByRunId={bridgeActivityByRunId}
                bridgeSpeechByRunId={bridgeSpeechByRunId}
              />
            ) : (
              <AppligatorFactoryGraphicsUnavailable />
            )
          )}
          {view === "appligatorOffice" && <AppligatorOffice onToast={flash} onError={fail} onOpenActionCenterRun={openActionCenterRun} />}
          {view === "implementationGuidelines" && <ImplementationGuidelines {...screenProps} />}
          {view === "documents" && <OperationsDocuments {...screenProps} />}
          {view === "projects" && <Projects {...screenProps} />}
          {view === "customers" && <Customers {...screenProps} />}
          {view === "calendar" && <CalendarPane db={db} currentEmployeeId={currentEmployeeId} title="Calendar" onToast={flash} onError={fail} />}
          {view === "email" && <Email {...screenProps} />}
          {view === "invoices" && <Invoices {...screenProps} />}
          {view === "expenses" && <Expenses {...screenProps} />}
          {view === "payments" && <Payments {...screenProps} />}
          {view === "employees" && <Employees {...screenProps} />}
          {view === "settings" && <Settings db={db} session={session} onToast={flash} onError={fail} onSave={handleSettings} onReload={reload} />}
        </div>
      </main>

      {drawer && <Drawer type={drawer.type} editId={drawer.editId} context={drawer.context} db={db} onClose={closeDrawer} onSubmit={handleSubmit} onAction={handleInvoiceAction} />}
      {chatWindow && chatWindow.type === "action-center" && ChatModal && (
        <ChatModal
          employeeId={chatWindow.employeeId}
          runId={chatWindow.runId}
          overview={actionCenterOverview}
          db={db}
          initialLiveActivity={chatWindow.initialLiveActivity || null}
          onClose={() => setChatWindow(null)}
          onToast={flash}
          onError={fail}
          onRefreshOverview={refreshActionCenterOverview}
        />
      )}
      {voiceAssistantOpen && <VoiceAssistantPanel db={db} view={view} viewLabel={(VIEW_META[view] && VIEW_META[view][0]) || view || "App"} pageContext={voicePageContext} onClose={() => setVoiceAssistantOpen(false)} onError={fail} />}
      {toast && <div className="toast"><Icon name="check" size={14} style={{ verticalAlign: "-2px", marginRight: 6 }} />{toast}</div>}

      <TweaksPanel>
        <TweakSection label="Navigation" />
        <TweakRadio label="Sidebar" value={sidebarTone} options={[{ value: "dark", label: "Dark" }, { value: "light", label: "Light" }]} onChange={(v) => setTweak("sidebar", v)} />
        <TweakSection label="Appearance" />
        <TweakColor label="Accent" value={t.accent} options={["#2856c4", "#0e7490", "#3f3f9e", "#15803d", "#1e293b"]} onChange={(v) => setTweak("accent", v)} />
        <TweakRadio label="Row density" value={t.density} options={[{ value: "compact", label: "Compact" }, { value: "regular", label: "Regular" }, { value: "comfortable", label: "Comfy" }]} onChange={(v) => setTweak("density", v)} />
      </TweaksPanel>
    </div>
  );
}

function BrandMark() {
  return <img src="/assets/appligator-icon.png" alt="" aria-hidden="true" />;
}

function LandingPage({ error, onSubmit }) {
  const [loginOpen, setLoginOpen] = React.useState(!!error);
  const openLogin = React.useCallback(() => setLoginOpen(true), []);
  const closeLogin = React.useCallback(() => setLoginOpen(false), []);
  useReveal();

  React.useEffect(() => {
    if (error) setLoginOpen(true);
  }, [error]);

  return (
    <div className="lp" data-accent="green" data-paper="warm">
      <LandingNav onLogin={openLogin} />
      <Hero />
      <Pipeline />
      <Services />
      <LeanDelivery />
      <TrustStrip />
      <LandingCTA onLogin={openLogin} />
      <LandingFooter />
      <LoginModal open={loginOpen} error={error} onClose={closeLogin} onSubmit={onSubmit} />
    </div>
  );
}

function LandingLogo() {
  return <span className="mark"><BrandMark /></span>;
}

function LandingIcon({ name, size = 20, stroke = 1.7, style }) {
  return <Icon name={name} size={size} stroke={stroke} style={style} />;
}

function useReveal() {
  React.useEffect(() => {
    const els = Array.from(document.querySelectorAll(".lp .reveal"));
    let raf = 0;
    const check = () => {
      raf = 0;
      const trigger = window.innerHeight * 0.92;
      for (let i = els.length - 1; i >= 0; i--) {
        const el = els[i];
        if (el.getBoundingClientRect().top < trigger) {
          el.classList.add("in");
          els.splice(i, 1);
        }
      }
      if (!els.length) {
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", onScroll);
      }
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(check); };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    check();
    const safety = setTimeout(() => els.slice().forEach((el) => el.classList.add("in")), 1600);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
      clearTimeout(safety);
    };
  }, []);
}

function LandingNav({ onLogin }) {
  const [scrolled, setScrolled] = React.useState(false);
  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={"lp-nav" + (scrolled ? " scrolled" : "")}>
      <div className="row">
        <a className="brand" href="#top" aria-label="Appligator home">
          <LandingLogo />
          <span style={{ display: "inline-flex", alignItems: "center" }}>Appligator<span className="tag">Impl</span></span>
        </a>
        <nav aria-label="Company">
          <a href="#process">Process</a>
          <a href="#services">Services</a>
          <a href="#delivery">Why lean</a>
          <a href="#hosting">Hosting</a>
        </nav>
        <div className="nav-actions">
          <button className="btn subtle" type="button" onClick={onLogin}>Internal log in</button>
          <a className="btn sm" href="#contact">Start a project <LandingIcon name="arrow" size={16} /></a>
        </div>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="hero" id="top">
      <div className="shell hero-grid">
        <div className="hero-copy">
          <span className="kicker reveal">Software implementation · small & medium projects</span>
          <h1 className="reveal d1">From first idea to <span className="accent">finished software</span>—on one lean line.</h1>
          <p className="lede reveal d1">
            Appligator turns a new project idea into a clear plan, a small useful core, and
            software your team can run, own, and keep improving. No enterprise drag. No detours.
          </p>
          <div className="hero-actions reveal d2">
            <a className="btn lg" href="#contact">Start a project <LandingIcon name="arrow" size={17} /></a>
            <a className="btn ghost lg" href="#process">See how we work</a>
          </div>
          <div className="hero-trust reveal d2">
            <span><span className="dot" /> Fixed, visible scope</span>
            <span><span className="dot" /> Built to hand over</span>
            <span><span className="dot" /> Hosting optional</span>
          </div>
        </div>
        <ProductPeek />
      </div>
    </section>
  );
}

function ProductPeek() {
  return (
    <div className="peek-wrap reveal d2">
      <div className="peek">
        <div className="peek-bar">
          <span className="dots"><i></i><i></i><i></i></span>
          <span className="url"><LandingIcon name="shield" size={11} /> app.appligator.dev/projects</span>
        </div>
        <div className="peek-body">
          <div className="peek-row">
            <div>
              <div className="peek-title">Orchard — inventory</div>
              <div className="peek-sub">Week 03 / 05 · on plan</div>
            </div>
            <span className="peek-pill"><span className="pdot"></span> In delivery</span>
          </div>
          <div className="peek-board">
            <div className="peek-col">
              <h5>Scope <b>done</b></h5>
              <div className="peek-card done"><span className="cl"><span>Workflow</span></span></div>
              <div className="peek-card done"><span className="cl"><span>Data model</span></span></div>
            </div>
            <div className="peek-col">
              <h5>Build <b>now</b></h5>
              <div className="peek-card"><span className="cl"><span>Core UI</span></span><div className="bar"><i style={{ width: "72%" }}></i></div></div>
              <div className="peek-card"><span className="cl"><span>API wiring</span></span><div className="bar"><i style={{ width: "40%" }}></i></div></div>
            </div>
            <div className="peek-col">
              <h5>Launch <b>next</b></h5>
              <div className="peek-card"><span className="cl"><span>Hosting</span></span></div>
            </div>
          </div>
          <div className="peek-meter">
            <span className="peek-sub" style={{ textTransform: "none", letterSpacing: 0 }}>Delivery</span>
            <span className="track"><i style={{ width: "64%" }}></i></span>
            <b>64%</b>
          </div>
        </div>
      </div>
      <div className="peek-float">
        <span className="pf-ic"><LandingIcon name="handover" size={16} /></span>
        <span className="pf-txt">
          <span className="pf-k">Handover</span>
          <span className="pf-v">You own the code</span>
        </span>
      </div>
    </div>
  );
}

const LANDING_STEPS = [
  ["01", "Idea", "We map the real workflow, the inputs, and what success looks like.", "Day 1-3"],
  ["02", "Plan", "A compact scope with visible milestones — no surprises, no bloat.", "Week 1"],
  ["03", "Build", "The useful core first, reviewed with you as it takes shape.", "Week 2-4"],
  ["04", "Launch", "Hosted, handed over, and documented. The code is yours.", "Week 5"],
];

function Pipeline() {
  const ref = React.useRef(null);
  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = 0;
    let done = false;
    const check = () => {
      raf = 0;
      if (done) return;
      if (el.getBoundingClientRect().top < window.innerHeight * 0.78) {
        el.classList.add("lit");
        done = true;
        window.removeEventListener("scroll", onScroll);
      }
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(check); };
    window.addEventListener("scroll", onScroll, { passive: true });
    check();
    const safety = setTimeout(() => el.classList.add("lit"), 2000);
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
      clearTimeout(safety);
    };
  }, []);

  return (
    <section className="pipeline-section" id="process">
      <div className="shell">
        <div className="pipeline-head">
          <div>
            <span className="kicker reveal">The lean line</span>
            <h2 className="reveal d1">Four steps. One direction. Idea to release in weeks, not quarters.</h2>
          </div>
          <p className="pl-note reveal d2">Every project follows the same slim path — so progress, decisions, and handover stay easy to see at any point.</p>
        </div>
        <div className="pipeline" ref={ref}>
          <span className="pl-progress"></span>
          {LANDING_STEPS.map(([n, title, body, week]) => (
            <div className="pl-node reveal" key={n}>
              <span className="pl-dot">{n}</span>
              <div className="pl-body">
                <span className="pl-week">{week}</span>
                <h3>{title}</h3>
                <p>{body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const LANDING_SERVICES = [
  ["projects", "Project implementation", "Custom workflows, dashboards, integrations, automations, and client portals built around one concrete business need."],
  ["handover", "Operational handover", "You keep the code in your own repositories, cloud accounts, and delivery process. No lock-in, ever."],
  ["server", "Managed hosting", "Prefer a single point of responsibility? Appligator can run hosting, monitoring, and updates for you."],
];

function Services() {
  return (
    <section className="section alt" id="services">
      <div className="shell">
        <div className="section-head">
          <span className="kicker reveal">What we do</span>
          <h2 className="reveal d1">From scope to shipped software—without turning every request into a programme.</h2>
        </div>
        <div className="services">
          {LANDING_SERVICES.map(([icon, title, body], i) => (
            <article className={"svc reveal d" + i} key={title} id={icon === "server" ? "hosting" : undefined}>
              <span className="svc-num mono">0{i + 1}</span>
              <span className="ic"><LandingIcon name={icon} size={22} /></span>
              <h3>{title}</h3>
              <p>{body}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

const LANDING_POINTS = [
  ["Compact scope, sharp focus", "We solve the agreed workflow first, then grow only where the business case is clear."],
  ["Visible milestones", "Direct feedback loops and weekly checkpoints keep delivery honest and predictable."],
  ["Built to be owned", "Clean, documented code you can keep, extend, or take in-house at any time."],
];

function LeanDelivery() {
  return (
    <section className="section" id="delivery">
      <div className="shell lean">
        <div className="lean-copy">
          <span className="kicker reveal">Delivery model</span>
          <h2 className="reveal d1">Small enough to stay sharp. Structured enough to rely on.</h2>
          <p className="reveal d1">
            Big programmes lose momentum in process. We keep teams small, scopes tight, and the
            line to launch short — so the software solving your workflow ships while it still matters.
          </p>
          <span className="own-chip reveal d2"><LandingIcon name="check" size={16} /> Your repositories. Your cloud. Your code.</span>
        </div>
        <div className="lean-points">
          {LANDING_POINTS.map(([heading, body], i) => (
            <div className={"lp-point reveal d" + i} key={heading}>
              <span className="n">{String(i + 1).padStart(2, "0")}</span>
              <div>
                <h4>{heading}</h4>
                <p>{body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const LANDING_STATS = [
  ["5", "weeks", "Typical idea-to-launch for a useful first version"],
  ["1", "team", "Small, senior, end-to-end — no handoffs between silos"],
  ["100%", "", "Of the code handed over and owned by the client"],
  ["0", "lock-in", "Self-host, switch, or take it in-house whenever you like"],
];

function TrustStrip() {
  return (
    <section className="section alt">
      <div className="shell">
        <div className="trust reveal">
          {LANDING_STATS.map(([value, unit, caption]) => (
            <div className="ts" key={caption}>
              <div className="v"><b>{value}</b>{unit && <span style={{ fontSize: ".5em", color: "var(--muted)", marginLeft: 4 }}>{unit}</span>}</div>
              <div className="k">{caption}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LandingCTA({ onLogin }) {
  return (
    <section className="cta-wrap" id="contact">
      <div className="shell">
        <div className="cta reveal">
          <div className="cta-copy">
            <span className="kicker">Start a project</span>
            <h2>Have an idea worth shipping? Let's map the lean line to launch.</h2>
            <p>Tell us the workflow you want to fix. We'll come back with a compact scope, a timeline, and a fixed first milestone—usually within a couple of days.</p>
          </div>
          <div className="cta-actions">
            <a className="btn lg" href="mailto:info@appligator.de">Book a scoping call <LandingIcon name="arrow" size={17} /></a>
            <button className="btn ghost lg" type="button" onClick={onLogin}>Internal log in</button>
          </div>
        </div>
      </div>
    </section>
  );
}

function LandingFooter() {
  return (
    <footer className="lp-foot">
      <div className="shell">
        <div className="row">
          <div>
            <a className="brand" href="#top"><LandingLogo /><span>Appligator</span></a>
            <p className="f-blurb">Software implementation for small and medium-sized projects. From first idea to finished, owned software—on one lean line.</p>
          </div>
          <div className="f-cols">
            <div className="f-col">
              <h5>Company</h5>
              <a href="#process">Process</a>
              <a href="#services">Services</a>
              <a href="#delivery">Why lean</a>
              <a href="#hosting">Hosting</a>
            </div>
            <div className="f-col">
              <h5>Get in touch</h5>
              <a href="mailto:info@appligator.de">info@appligator.de</a>
              <a href="#contact">Start a project</a>
            </div>
          </div>
        </div>
        <div className="f-base">
          <span className="mono">© {new Date().getFullYear()} Appligator Company</span>
          <span className="mono">01000001 · idea → release</span>
        </div>
      </div>
    </footer>
  );
}

function LoginModal({ open, error, onClose, onSubmit }) {
  const lastOrganizationSlug = () => {
    try { return window.localStorage.getItem("appligator.organizationSlug") || "appligator"; }
    catch (_) { return "appligator"; }
  };
  const [form, setForm] = React.useState({ organizationSlug: lastOrganizationSlug(), email: "", password: "" });
  const [busy, setBusy] = React.useState(false);
  const set = (key, value) => setForm((current) => ({ ...current, [key]: value }));

  React.useEffect(() => {
    if (!open) return;
    const onKey = (event) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    try {
      await onSubmit({ ...form, organizationSlug: (form.organizationSlug || "appligator").trim().toLowerCase() });
      try { window.localStorage.setItem("appligator.organizationSlug", (form.organizationSlug || "appligator").trim().toLowerCase()); } catch (_) {}
    }
    finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="login-overlay" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <form className="login-card" onSubmit={submit}>
        <div className="lc-head">
          <span className="mark"><BrandMark /></span>
          <div><strong>Appligator Company</strong><small>Internal operations</small></div>
          <button className="lc-close" type="button" onClick={onClose} aria-label="Close"><LandingIcon name="close" size={16} /></button>
        </div>
        {error && <div className="alert">{error}</div>}
        <div className="field"><label>Organization</label><input value={form.organizationSlug} onChange={(event) => set("organizationSlug", event.target.value)} placeholder="appligator" autoComplete="organization" pattern="(?:[a-z0-9]|-){3,40}" required /></div>
        <div className="field"><label>Email</label><input type="email" value={form.email} onChange={(event) => set("email", event.target.value)} placeholder="info@appligator.de" autoComplete="email" required /></div>
        <div className="field"><label>Password</label><input type="password" value={form.password} onChange={(event) => set("password", event.target.value)} autoComplete="current-password" minLength={8} required /></div>
        <button className="btn lg" type="submit" style={{ width: "100%" }} disabled={busy}>{busy ? "Signing in..." : "Sign in"}</button>
        <div className="lc-foot">Operations workspace · authorised staff only</div>
      </form>
    </div>
  );
}

function hexToRgb(h) { const n = parseInt(h.slice(1), 16); return [(n >> 16) & 255, (n >> 8) & 255, n & 255]; }
function shade(h, pct) { const [r, g, b] = hexToRgb(h); const f = (1 + pct / 100); const c = (v) => Math.max(0, Math.min(255, Math.round(v * f))); return `rgb(${c(r)},${c(g)},${c(b)})`; }
function tint(h, amt) { const [r, g, b] = hexToRgb(h); const m = (v) => Math.round(v + (255 - v) * amt); return `rgb(${m(r)},${m(g)},${m(b)})`; }

ReactDOM.createRoot(document.getElementById("root")).render(<App />);
